<template>
  <canvas ref="canvasRef" class="hero-canvas" aria-hidden="true"></canvas>
</template>

<script setup>
import { onBeforeUnmount, onMounted, ref } from 'vue'

const canvasRef = ref(null)
let frameId = 0
let resizeObserver = null

// --- Stars ---
let stars = []
const STAR_COUNT = 500

// --- Cosmic dust ---
let dustParticles = []
const DUST_COUNT = 80

// --- Nebulae ---
const nebulae = [
  { x: 0.18, y: 0.25, r: 0.35, color: [14, 48, 76], alpha: 0.10, drift: 0.0003 },
  { x: 0.80, y: 0.60, r: 0.30, color: [10, 32, 58], alpha: 0.08, drift: -0.0004 },
  { x: 0.48, y: 0.44, r: 0.38, color: [18, 56, 88], alpha: 0.06, drift: 0.0002 },
  { x: 0.62, y: 0.32, r: 0.26, color: [30, 20, 60], alpha: 0.05, drift: -0.0005 },
  { x: 0.35, y: 0.70, r: 0.28, color: [8, 40, 68], alpha: 0.07, drift: 0.00035 },
]

// --- Meteors ---
let meteors = []
const MAX_METEORS = 3
let meteorTimer = 0

// --- Central wormhole ---
const centerGlow = { x: 0.50, y: 0.48 }

// --- Accretion disk particles ---
let diskParticles = []
const DISK_PARTICLE_COUNT = 60

function seedStars(w, h) {
  stars = []
  for (let i = 0; i < STAR_COUNT; i++) {
    const angle = Math.random() * Math.PI * 2
    const dist = Math.random() * Math.min(w, h) * 0.75
    // 3 tiers of stars
    const tier = Math.random()
    let r, baseAlpha, twinkleSpeed
    if (tier < 0.6) {
      // Tiny background stars
      r = Math.random() * 1.0 + 0.2
      baseAlpha = Math.random() * 0.3 + 0.2
      twinkleSpeed = Math.random() * 0.01 + 0.003
    } else if (tier < 0.9) {
      // Medium stars
      r = Math.random() * 1.6 + 0.8
      baseAlpha = Math.random() * 0.4 + 0.4
      twinkleSpeed = Math.random() * 0.02 + 0.006
    } else {
      // Bright foreground stars
      r = Math.random() * 2.5 + 1.5
      baseAlpha = Math.random() * 0.3 + 0.6
      twinkleSpeed = Math.random() * 0.03 + 0.01
    }
    stars.push({
      x: w * 0.5 + Math.cos(angle) * dist,
      y: h * 0.48 + Math.sin(angle) * dist * 0.6,
      r,
      twinkleSpeed,
      twinkleOffset: Math.random() * Math.PI * 2,
      baseAlpha,
      hue: Math.random() < 0.15 ? 195 + Math.random() * 50 : 0,
    })
  }
}

function seedDust(w, h) {
  dustParticles = []
  for (let i = 0; i < DUST_COUNT; i++) {
    dustParticles.push({
      x: Math.random() * w,
      y: Math.random() * h,
      r: Math.random() * 0.6 + 0.15,
      alpha: Math.random() * 0.25 + 0.05,
      vx: (Math.random() - 0.5) * 0.06,
      vy: (Math.random() - 0.5) * 0.04,
    })
  }
}

function seedDisk(w, h) {
  diskParticles = []
  const cx = w * centerGlow.x
  const cy = h * centerGlow.y
  const ringR = Math.min(w, h) * 0.22
  for (let i = 0; i < DISK_PARTICLE_COUNT; i++) {
    const angle = Math.random() * Math.PI * 2
    const dist = ringR * (0.6 + Math.random() * 0.8)
    diskParticles.push({
      angle,
      dist,
      r: Math.random() * 1.2 + 0.3,
      alpha: Math.random() * 0.4 + 0.15,
      speed: (Math.random() * 0.0004 + 0.0002) * (Math.random() < 0.5 ? 1 : -1),
    })
  }
}

function spawnMeteor(w, h) {
  const fromLeft = Math.random() < 0.5
  const startX = fromLeft ? Math.random() * w * 0.35 : w * 0.65 + Math.random() * w * 0.35
  const startY = Math.random() * h * 0.45
  meteors.push({
    x: startX,
    y: startY,
    vx: (Math.random() * 4 + 3) * (fromLeft ? 1 : -1),
    vy: (Math.random() * 2 + 1.2) * (fromLeft ? 1 : -1),
    life: 1.0,
    decay: Math.random() * 0.006 + 0.004,
    length: Math.random() * 80 + 40,
  })
}

function drawStarfield(ctx, w, h, time) {
  // Deep space — richer gradient
  const bg = ctx.createRadialGradient(w * 0.5, h * 0.48, 0, w * 0.5, h * 0.48, Math.max(w, h) * 0.85)
  bg.addColorStop(0, '#0b1828')
  bg.addColorStop(0.35, '#061018')
  bg.addColorStop(0.7, '#030a12')
  bg.addColorStop(1, '#010508')
  ctx.fillStyle = bg
  ctx.fillRect(0, 0, w, h)

  // --- Nebulae ---
  nebulae.forEach((n) => {
    const nx = w * n.x + Math.sin(time * n.drift) * w * 0.05
    const ny = h * n.y + Math.cos(time * n.drift * 1.3) * h * 0.04
    const nr = Math.min(w, h) * n.r
    const glow = ctx.createRadialGradient(nx, ny, 0, nx, ny, nr)
    const [r, g, b] = n.color
    glow.addColorStop(0, `rgba(${r},${g},${b},${n.alpha})`)
    glow.addColorStop(0.4, `rgba(${r},${g},${b},${n.alpha * 0.6})`)
    glow.addColorStop(0.7, `rgba(${r},${g},${b},${n.alpha * 0.2})`)
    glow.addColorStop(1, 'rgba(0,0,0,0)')
    ctx.fillStyle = glow
    ctx.fillRect(0, 0, w, h)
  })

  // --- Central wormhole ring (enhanced Gargantua) ---
  const cx = w * centerGlow.x
  const cy = h * centerGlow.y
  const ringR = Math.min(w, h) * 0.22

  ctx.save()

  // Outer ring — more pronounced, sci-fi glow
  const outerRing = ctx.createRadialGradient(cx, cy, ringR * 0.55, cx, cy, ringR * 1.4)
  outerRing.addColorStop(0, 'rgba(0,0,0,0)')
  outerRing.addColorStop(0.4, 'rgba(60,170,210,0.06)')
  outerRing.addColorStop(0.5, 'rgba(80,210,235,0.09)')
  outerRing.addColorStop(0.6, 'rgba(60,180,220,0.05)')
  outerRing.addColorStop(0.75, 'rgba(40,140,180,0.03)')
  outerRing.addColorStop(1, 'rgba(0,0,0,0)')
  ctx.fillStyle = outerRing
  ctx.fillRect(0, 0, w, h)

  // Inner shadow (black hole center)
  const shadow = ctx.createRadialGradient(cx, cy, 0, cx, cy, ringR * 0.45)
  shadow.addColorStop(0, 'rgba(2,4,8,0.45)')
  shadow.addColorStop(0.6, 'rgba(4,8,16,0.15)')
  shadow.addColorStop(1, 'rgba(0,0,0,0)')
  ctx.fillStyle = shadow
  ctx.fillRect(0, 0, w, h)

  // Thin bright ring edge (event horizon glow)
  ctx.strokeStyle = 'rgba(100,210,240,0.08)'
  ctx.lineWidth = 1.5
  ctx.beginPath()
  ctx.arc(cx, cy, ringR * 0.55, 0, Math.PI * 2)
  ctx.stroke()

  ctx.restore()

  // --- Accretion disk particles ---
  diskParticles.forEach((dp) => {
    dp.angle += dp.speed
    const px = cx + Math.cos(dp.angle) * dp.dist
    const py = cy + Math.sin(dp.angle) * dp.dist * 0.35
    ctx.save()
    ctx.globalAlpha = dp.alpha * (0.7 + 0.3 * Math.sin(time * 0.005 + dp.angle))
    ctx.fillStyle = '#a0e8ff'
    ctx.beginPath()
    ctx.arc(px, py, dp.r, 0, Math.PI * 2)
    ctx.fill()
    ctx.restore()
  })

  // --- Cosmic dust ---
  dustParticles.forEach((dp) => {
    dp.x += dp.vx
    dp.y += dp.vy
    // Wrap around
    if (dp.x < -10) dp.x = w + 10
    if (dp.x > w + 10) dp.x = -10
    if (dp.y < -10) dp.y = h + 10
    if (dp.y > h + 10) dp.y = -10

    ctx.save()
    ctx.globalAlpha = dp.alpha
    ctx.fillStyle = '#c8ddf8'
    ctx.beginPath()
    ctx.arc(dp.x, dp.y, dp.r, 0, Math.PI * 2)
    ctx.fill()
    ctx.restore()
  })

  // --- Stars ---
  stars.forEach((star) => {
    const twinkle = Math.sin(time * star.twinkleSpeed + star.twinkleOffset) * 0.4 + 0.6
    const alpha = star.baseAlpha * twinkle
    const hue = star.hue || 0

    ctx.save()
    ctx.globalAlpha = alpha

    // Glow halo for medium+ stars
    if (star.r > 1.0) {
      const glowSize = star.r * 3.5
      const glow = ctx.createRadialGradient(star.x, star.y, 0, star.x, star.y, glowSize)
      if (hue > 0) {
        glow.addColorStop(0, `hsla(${hue},55%,80%,${alpha * 0.55})`)
        glow.addColorStop(1, 'rgba(0,0,0,0)')
      } else {
        glow.addColorStop(0, `rgba(210,230,255,${alpha * 0.45})`)
        glow.addColorStop(1, 'rgba(0,0,0,0)')
      }
      ctx.fillStyle = glow
      ctx.beginPath()
      ctx.arc(star.x, star.y, glowSize, 0, Math.PI * 2)
      ctx.fill()
    }

    // Star core
    ctx.fillStyle = hue > 0
      ? `hsla(${hue},40%,92%,${alpha})`
      : `rgba(215,235,255,${alpha})`
    ctx.beginPath()
    ctx.arc(star.x, star.y, star.r, 0, Math.PI * 2)
    ctx.fill()
    ctx.restore()
  })

  // --- Meteors with trail glow ---
  meteors.forEach((m) => {
    ctx.save()
    const endX = m.x - m.vx * m.length * 0.015
    const endY = m.y - m.vy * m.length * 0.015
    const grad = ctx.createLinearGradient(m.x, m.y, endX, endY)
    grad.addColorStop(0, `rgba(255,255,255,${m.life * 0.95})`)
    grad.addColorStop(0.1, `rgba(200,230,255,${m.life * 0.7})`)
    grad.addColorStop(0.4, `rgba(140,200,240,${m.life * 0.3})`)
    grad.addColorStop(1, 'rgba(100,160,220,0)')
    ctx.strokeStyle = grad
    ctx.lineWidth = 2.2
    ctx.lineCap = 'round'
    ctx.beginPath()
    ctx.moveTo(m.x, m.y)
    ctx.lineTo(endX, endY)
    ctx.stroke()

    // Head glow
    const headGlow = ctx.createRadialGradient(m.x, m.y, 0, m.x, m.y, 5)
    headGlow.addColorStop(0, `rgba(255,255,255,${m.life * 0.9})`)
    headGlow.addColorStop(1, 'rgba(255,255,255,0)')
    ctx.fillStyle = headGlow
    ctx.beginPath()
    ctx.arc(m.x, m.y, 5, 0, Math.PI * 2)
    ctx.fill()
    ctx.restore()
  })
}

function updateMeteors() {
  meteors.forEach((m) => {
    m.x += m.vx
    m.y += m.vy
    m.life -= m.decay
  })
  meteors = meteors.filter((m) => m.life > 0)
}

function drawScene(canvas, time) {
  const ctx = canvas.getContext('2d')
  const { width, height } = canvas
  drawStarfield(ctx, width, height, time)

  meteorTimer++
  if (meteorTimer > 150 && meteors.length < MAX_METEORS && Math.random() < 0.02) {
    spawnMeteor(width, height)
    meteorTimer = 0
  }
  updateMeteors()
}

function resizeCanvas(canvas) {
  const ratio = window.devicePixelRatio || 1
  const bounds = canvas.getBoundingClientRect()
  const nextWidth = Math.round(bounds.width * ratio)
  const nextHeight = Math.round(bounds.height * ratio)
  if (canvas.width !== nextWidth || canvas.height !== nextHeight) {
    canvas.width = nextWidth
    canvas.height = nextHeight
    seedStars(nextWidth, nextHeight)
    seedDust(nextWidth, nextHeight)
    seedDisk(nextWidth, nextHeight)
  }
}

function animate(time) {
  const canvas = canvasRef.value
  if (canvas) {
    resizeCanvas(canvas)
    drawScene(canvas, time)
  }
  frameId = requestAnimationFrame(animate)
}

onMounted(() => {
  const canvas = canvasRef.value
  if (!canvas) return
  resizeObserver = new ResizeObserver(() => resizeCanvas(canvas))
  resizeObserver.observe(canvas)
  resizeCanvas(canvas)
  animate(0)
})

onBeforeUnmount(() => {
  cancelAnimationFrame(frameId)
  resizeObserver?.disconnect()
})
</script>
